import { createQuery, type World } from 'koota';
import { emit, getContext } from '../context';
import { Crown, Dead, MatchState, Player, RoundPhase, RoundState } from '../traits';
import { nextMatchLevel, respawnPlayers } from '../systems/reset';
import { openDrops } from './spawner';
import { modeHasTeams, startPartyRound, stepPartyRules, teamForSlot } from './party';
import { PartyPlayer, PartyState } from '../traits';
import { MATCH_WIN_KEYS } from './scores';

const playersQ = createQuery(Player);

export function rules(world: World): void {
  const ctx = getContext(world);
  const round = world.get(RoundState);
  const match = world.get(MatchState);
  if (!round || !match) return;

  const hillWinner = round.phase === RoundPhase.Fighting ? stepPartyRules(world) : null;
  const living: number[] = [];
  world.query(playersQ).updateEach(([p], e) => {
    if (!e.has(Dead) && ctx.players.includes(e)) living.push(p.slot);
  });
  round.aliveMask = living.reduce((m, s) => m | (1 << s), 0);
  round.ticks += 1;

  if (round.phase === RoundPhase.Loading) {
    if (round.ticks <= 1 && match.round > 0) {
      nextMatchLevel(world);
      respawnPlayers(world);
      // Pick up the level index the rotation just stored; the copy above predates it.
      const fresh = world.get(MatchState);
      if (fresh) match.levelIndex = fresh.levelIndex;
    }
    startPartyRound(world);
    round.phase = RoundPhase.Countdown;
    round.ticks = 0;
    round.winner = -1;
    emit(world, { type: 'round-phase', phase: 'countdown' });
  } else if (round.phase === RoundPhase.Countdown) {
    if (round.ticks >= ctx.tuning.countdownTicks) {
      round.phase = RoundPhase.Fighting;
      round.ticks = 0;
      const party = world.get(PartyState)!;
      world.set(PartyState, {
        ...party,
        nextWeaponTick: ctx.tick + ctx.settings.rouletteSeconds * ctx.tuning.tickRate,
      });
      openDrops(world);
      emit(world, { type: 'round-phase', phase: 'fighting' });
    }
  } else if (round.phase === RoundPhase.Fighting) {
    const teams = modeHasTeams(ctx.settings.mode);
    const aliveTeams = new Set(living.map((slot) => teamForSlot(ctx.settings, slot)));
    const waiting = ctx.players.some((player) => (player.get(PartyPlayer)?.respawnTicks ?? 0) > 0);
    const elimination =
      ctx.settings.mode !== 'king-of-hill' &&
      ctx.settings.mode !== 'arms-race' &&
      !waiting &&
      (teams ? aliveTeams.size <= 1 : living.length <= 1) &&
      ctx.players.length > 1;
    if (hillWinner !== null || elimination) {
      round.phase = RoundPhase.LastKill;
      round.ticks = 0;
      if (hillWinner !== null || living.length >= 1) {
        const slot = hillWinner ?? living[0]!;
        round.winner = slot;
        const key = MATCH_WIN_KEYS[slot]!;
        const winners = teams
          ? ctx.players
              .map((player) => player.get(Player)!.slot)
              .filter(
                (other) => teamForSlot(ctx.settings, other) === teamForSlot(ctx.settings, slot),
              )
          : [slot];
        for (const winner of winners) {
          const scoreKey = MATCH_WIN_KEYS[winner]!;
          match[scoreKey] += 1;
          emit(world, { type: 'score', slot: winner, wins: match[scoreKey] });
        }
        world.query(Player).updateEach(([p], e) => {
          if (winners.includes(p.slot)) e.add(Crown());
          else if (e.has(Crown)) e.remove(Crown);
        });
        if (
          ctx.settings.mode === 'arms-race' ||
          (match.firstTo > 0 && match[key] >= match.firstTo)
        ) {
          round.phase = RoundPhase.MatchOver;
          emit(world, { type: 'round-phase', phase: 'match-over' });
        }
      } else {
        emit(world, { type: 'round-phase', phase: 'draw' });
      }
      if (round.phase === RoundPhase.LastKill)
        emit(world, { type: 'round-phase', phase: 'last-kill' });
    }
  } else if (round.phase === RoundPhase.LastKill) {
    if (round.ticks >= ctx.tuning.slowmoTicks) {
      round.phase = RoundPhase.Scoreboard;
      round.ticks = 0;
      emit(world, { type: 'round-phase', phase: 'scoreboard' });
    }
  } else if (round.phase === RoundPhase.Scoreboard) {
    if (round.ticks >= ctx.tuning.scoreboardTicks) {
      round.phase = RoundPhase.Loading;
      round.ticks = 0;
      match.round += 1;
      emit(world, { type: 'round-phase', phase: 'loading' });
    }
  }

  world.set(RoundState, round);
  world.set(MatchState, match);
}

/** Players may move during the countdown but nobody can hurt anybody until "FIGHT". */
export function combatAllowed(world: World): boolean {
  const phase = world.get(RoundState)?.phase ?? RoundPhase.Fighting;
  return phase !== RoundPhase.Loading && phase !== RoundPhase.Countdown;
}

export function stepScaleForPhase(world: World): number {
  const round = world.get(RoundState);
  if (round?.phase === RoundPhase.LastKill) return getContext(world).tuning.lastKillSlowmo;
  return 1;
}
